import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRight, GraduationCap, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useSearchParams } from 'react-router'
import { toApiError } from '@/api/errors'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { usePlatformAuth } from '@/features/platform/usePlatformAuth'
import { operatorLoginSchema, type OperatorLoginForm } from '@/features/platform/schemas'
import { LanguagePicker } from '@/features/preferences/components/PreferenceControls'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

/** Sign-in for platform operators — separate from the schools' login page. */
export function PlatformLoginPage() {
  const { t } = useTranslation('platform')
  useDocumentTitle('Platform sign-in')
  const { status, login } = usePlatformAuth()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const next = params.get('next')
  const target = next?.startsWith('/platform') ? next : '/platform'
  const [error, setError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OperatorLoginForm>({
    resolver: zodResolver(operatorLoginSchema),
    defaultValues: { email: '', password: '' },
  })

  if (status === 'authenticated') return <Navigate to={target} replace />

  const onSubmit = handleSubmit(async (v) => {
    setError(null)
    try {
      await login(v.email, v.password)
      void navigate(target, { replace: true })
    } catch (e) {
      setError(toApiError(e).message)
    }
  })

  return (
    <div className="relative flex min-h-dvh items-center justify-center bg-canvas px-4 py-10">
      <div className="absolute top-4 right-4">
        <LanguagePicker compact />
      </div>
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="bg-brand-gradient grid size-12 place-items-center rounded-2xl text-white shadow-raised">
            <GraduationCap className="size-6" aria-hidden="true" />
          </span>
          <h1 className="mt-4 text-xl font-semibold tracking-tight text-ink">{t('SchoolEduca platform')}</h1>
          <p className="mt-1 text-sm text-muted">{t('Operator console: create and manage the schools.')}</p>
        </div>
        <Card className="p-6">
          <form onSubmit={(e) => void onSubmit(e)} noValidate className="space-y-4">
            {error ? (
              <Alert tone="danger" title={t('Sign-in failed')}>
                {error}
              </Alert>
            ) : null}
            <FormField label={t('Email')} error={errors.email?.message} required>
              <Input type="email" autoComplete="username" autoFocus {...register('email')} />
            </FormField>
            <FormField label={t('Password')} error={errors.password?.message} required>
              <Input type="password" autoComplete="current-password" {...register('password')} />
            </FormField>
            <Button type="submit" className="w-full" loading={isSubmitting} rightIcon={<ArrowRight />}>
              {t('Sign in')}
            </Button>
          </form>
        </Card>
        <p className="mt-5 flex items-center justify-center gap-1.5 text-xs text-muted">
          <ShieldCheck className="size-3.5" aria-hidden="true" />
          {t('Operators never see the schools’ students, staff or finances.')}
        </p>
      </div>
    </div>
  )
}
